import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  Search,
  Filter,
  Code,
  Database,
  FileInput,
  FileOutput,
  GitBranch,
} from 'lucide-react';
import { EngineRegistry } from '../engines/EngineRegistry';
import type { OperationWithEngine } from '../data/operations';
import { getAllEngines, getOperationsByEngine } from '../data/operations';
import {
  PageShell, Section, SectionLabel, Heading,
  Card, CardHeader, RowList, Row, EmptyState,
} from '@/components/elevation';

export const Operations: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEngine, setSelectedEngine] = useState<string | null>(null);
  const [expandedOperations, setExpandedOperations] = useState<Set<string>>(new Set());

  EngineRegistry.getInstance();
  const allEngines = getAllEngines();
  const allOperations = getOperationsByEngine('');

  const filteredOperations = allOperations.filter(op => {
    const matchesSearch =
      searchTerm === '' ||
      op.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      op.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      op.engineName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEngine = selectedEngine === null || op.engineId === selectedEngine;
    return matchesSearch && matchesEngine;
  });

  const toggleOperation = (id: string) => {
    const next = new Set(expandedOperations);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpandedOperations(next);
  };

  const operationsByEngine = filteredOperations.reduce((acc, op) => {
    if (!acc[op.engineId]) acc[op.engineId] = [];
    acc[op.engineId].push(op);
    return acc;
  }, {} as Record<string, OperationWithEngine[]>);

  return (
    <PageShell>
      {/* Back */}
      <Link to="/elevation/engines" className="inline-flex items-center gap-1.5 text-xs text-charcoal-500 dark:text-charcoal-400 hover:text-primary-600 mb-6">
        <ArrowLeft className="h-3.5 w-3.5" />
        Platform Engines
      </Link>

      {/* Header */}
      <Section>
        <div className="flex items-center gap-2 mb-1">
          <Settings className="h-4 w-4 text-charcoal-400" />
          <SectionLabel className="mb-0">Infrastructure</SectionLabel>
        </div>
        <Heading as="h1" size="xl" className="mb-2">Operations</Heading>
        <p className="text-sm text-charcoal-500 dark:text-charcoal-400 leading-relaxed">
          Operations are the fundamental units of work in the system. Each operation takes inputs,
          performs a transformation, and produces outputs.
        </p>
      </Section>

      {/* Search and Filter */}
      <Section>
        <Card>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-400" />
              <input
                type="text"
                placeholder="Search by name, description, or engine…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-charcoal-200 dark:border-charcoal-700 bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-charcoal-900 dark:text-charcoal-50 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-400" />
              <select
                value={selectedEngine || ''}
                onChange={(e) => setSelectedEngine(e.target.value || null)}
                className="pl-9 pr-8 py-2 text-sm border border-charcoal-200 dark:border-charcoal-700 bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-charcoal-700 dark:text-charcoal-200 focus:outline-none focus:ring-2 focus:ring-primary-200 appearance-none"
              >
                <option value="">All Engines</option>
                {allEngines.map(engine => (
                  <option key={engine.id} value={engine.id}>{engine.name}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-charcoal-400 mt-3">
            {filteredOperations.length} of {allOperations.length} operations
          </p>
        </Card>
      </Section>

      {/* Operations List */}
      <div className="space-y-6">
        {Object.entries(operationsByEngine).map(([engineId, operations]) => {
          const engine = allEngines.find(e => e.id === engineId);
          if (!engine) return null;
          return (
            <Card key={engineId} flush>
              <CardHeader title={engine.name} subtitle={engine.purpose} count={`${operations.length} ops`} />
              <RowList>
                {operations.map((operation) => {
                  const isExpanded = expandedOperations.has(operation.id);
                  return (
                    <div key={operation.id}>
                      <Row onClick={() => toggleOperation(operation.id)} className="py-3.5">
                        {isExpanded
                          ? <ChevronDown className="h-4 w-4 text-charcoal-400 shrink-0" />
                          : <ChevronRight className="h-4 w-4 text-charcoal-400 shrink-0" />}
                        <Code className="h-4 w-4 text-charcoal-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-heading font-semibold text-charcoal-900 dark:text-charcoal-50 text-sm">{operation.name}</p>
                          <p className="text-xs text-charcoal-500 dark:text-charcoal-400 truncate mt-0.5">{operation.description}</p>
                        </div>
                      </Row>
                      {isExpanded && (
                        <div className="px-5 pb-5 pt-3 bg-charcoal-50 dark:bg-[var(--bg-raised)] border-t border-charcoal-100 dark:border-charcoal-800 space-y-4">
                          <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <FileInput className="h-3.5 w-3.5 text-charcoal-400" />
                              <span className="text-xs font-mono uppercase tracking-wider text-charcoal-500">Input</span>
                            </div>
                            <code className="block text-xs font-mono text-charcoal-700 dark:text-charcoal-300 bg-[var(--bg-raised)] dark:bg-[var(--surface)] rounded px-3 py-2 border border-charcoal-200 dark:border-charcoal-700">
                              {operation.inputSchema}
                            </code>
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <GitBranch className="h-3.5 w-3.5 text-charcoal-400" />
                              <span className="text-xs font-mono uppercase tracking-wider text-charcoal-500">Transformation</span>
                            </div>
                            <p className="text-sm text-charcoal-700 dark:text-charcoal-300 bg-[var(--bg-raised)] dark:bg-[var(--surface)] rounded px-3 py-2 border border-charcoal-200 dark:border-charcoal-700">
                              {operation.description}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <FileOutput className="h-3.5 w-3.5 text-charcoal-400" />
                              <span className="text-xs font-mono uppercase tracking-wider text-charcoal-500">Output</span>
                            </div>
                            <code className="block text-xs font-mono text-charcoal-700 dark:text-charcoal-300 bg-[var(--bg-raised)] dark:bg-[var(--surface)] rounded px-3 py-2 border border-charcoal-200 dark:border-charcoal-700">
                              {operation.outputSchema}
                            </code>
                          </div>
                          {operation.example && (
                            <div>
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Database className="h-3.5 w-3.5 text-charcoal-400" />
                                <span className="text-xs font-mono uppercase tracking-wider text-charcoal-500">Example</span>
                              </div>
                              <code className="block text-xs font-mono text-charcoal-700 dark:text-charcoal-300 bg-[var(--bg-raised)] dark:bg-[var(--surface)] rounded px-3 py-2 border border-charcoal-200 dark:border-charcoal-700 whitespace-pre-wrap">
                                {operation.example}
                              </code>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </RowList>
            </Card>
          );
        })}

        {filteredOperations.length === 0 && (
          <Card>
            <EmptyState
              title="No operations found"
              description="Try adjusting your search or filter criteria."
            />
          </Card>
        )}
      </div>
    </PageShell>
  );
};
